import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { clientApi, getApiErrorMessage } from "@/lib/api";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { User, Mail, Phone, IdCard, UserPlus, Trash2, Save, Globe2, FileUp, FileText } from "lucide-react";

const SR_KINDS = [
  ["bank_change", "Bank Account Change"],
  ["contact_change", "Contact Details Change"],
  ["nominee_change", "Nominee Change"],
];

function ServiceRequestsCard() {
  const [requests, setRequests] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ kind: "bank_change", rta: "cams", amc_code: "", folio_no: "", f1: "", f2: "", f3: "" });

  const load = () => clientApi.get("/service-requests").then((r) => setRequests(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const fieldDefs = {
    bank_change: [["f1", "New account number", "pb_act_no"], ["f2", "New IFSC", "pb_ifsc_cd"], ["f3", "Account holder name", "pb_hld_nam"]],
    contact_change: [["f1", "New mobile", "new_mobile"], ["f2", "New email", "new_email"]],
    nominee_change: [["f1", "Nominee name", "nominee_name"], ["f2", "Nominee PAN", "nominee_pan"], ["f3", "Allocation %", "nominee_percent"]],
  }[form.kind];

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fields = {};
      fieldDefs.forEach(([k, , apiKey]) => { if (form[k]) fields[apiKey] = form[k]; });
      const { data } = await clientApi.post("/service-requests", {
        kind: form.kind, rta: form.rta, amc_code: form.amc_code, folio_no: form.folio_no, fields,
      });
      toast.success(data.bse2?.message || "Service request submitted");
      setForm({ kind: form.kind, rta: "cams", amc_code: "", folio_no: "", f1: "", f2: "", f3: "" });
      load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="glass-card mt-6 max-w-2xl border-0">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-heading text-lg text-[var(--brand)]"><FileText className="h-4 w-4" /> Service Requests (direct to RTA)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <form className="space-y-3" onSubmit={submit}>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Request type</Label>
              <select value={form.kind} onChange={upd("kind")} data-testid="sr-kind-select"
                className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm">
                {SR_KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="space-y-1.5"><Label>RTA</Label>
              <select value={form.rta} onChange={upd("rta")} data-testid="sr-rta-select"
                className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm">
                <option value="cams">CAMS</option>
                <option value="kfin">KFintech</option>
              </select>
            </div>
            <div className="space-y-1.5"><Label>AMC code</Label><Input required value={form.amc_code} onChange={upd("amc_code")} data-testid="sr-amc-input" /></div>
            <div className="space-y-1.5"><Label>Folio number</Label><Input required value={form.folio_no} onChange={upd("folio_no")} data-testid="sr-folio-input" /></div>
            {fieldDefs.map(([k, label]) => (
              <div key={k} className="space-y-1.5"><Label>{label}</Label><Input value={form[k]} onChange={upd(k)} data-testid={`sr-${k}-input`} /></div>
            ))}
          </div>
          <Button type="submit" disabled={saving} className="rounded-full bg-[var(--cta)]" data-testid="sr-submit-button">
            {saving ? "Submitting..." : "Submit request"}
          </Button>
        </form>
        {requests.length > 0 && (
          <div className="divide-y divide-[var(--glass-border)] border-t border-[var(--glass-border)] pt-2 text-sm">
            {requests.map((r) => (
              <div key={r.id} className="flex items-center justify-between py-2" data-testid={`sr-row-${r.id}`}>
                <div>
                  <div className="font-medium">{SR_KINDS.find(([v]) => v === r.kind)?.[1] || r.kind} · {r.rta.toUpperCase()} · Folio {r.folio_no}</div>
                  <div className="text-xs text-[var(--ink-muted)]">{r.created_at?.slice(0, 10)} · {r.bse2?.message || r.status}</div>
                </div>
                <StatusBadge status={r.bse2?.status || r.status} />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const EMPTY_NOMINEE = { name: "", relationship: "Spouse", allocation_percentage: 100, pan: "", mobile: "", email: "", id_proof_type: "PAN", id_proof_number: "", is_minor: false, guardian_name: "", guardian_pan: "", guardian_relationship: "" };

const SOURCE_OF_WEALTH = [["01", "Salary"], ["02", "Business Income"], ["03", "Gift"], ["04", "Ancestral Property"], ["05", "Rental Income"], ["06", "Prize Money"], ["07", "Royalty"], ["08", "Others"]];
const INCOME_SLABS = [["31", "Below 1 Lakh"], ["32", "1-5 Lakhs"], ["33", "5-10 Lakhs"], ["34", "10-25 Lakhs"], ["35", "25 Lakhs - 1 Crore"], ["36", "Above 1 Crore"]];

function FatcaCard({ user }) {
  const [form, setForm] = useState({
    inv_name: user?.full_name || "", po_bir_inc: "", co_bir_inc: "India",
    srce_wealt: "01", inc_slab: "31", pep_flag: "N", occ_type: "S", new_change: "N",
    tax_residencies: [{ country: "India", tpin: user?.pan || "", id_type: "C" }],
  });
  const [loading, setLoading] = useState(false);
  const [existing, setExisting] = useState(false);

  useEffect(() => {
    clientApi.get("/fatca").then((r) => {
      if (r.data) {
        setExisting(true);
        setForm((f) => ({
          ...f, inv_name: r.data.inv_name, po_bir_inc: r.data.po_bir_inc, co_bir_inc: r.data.co_bir_inc,
          srce_wealt: r.data.srce_wealt, inc_slab: r.data.inc_slab, pep_flag: r.data.pep_flag,
          occ_type: r.data.occ_type, new_change: "C",
          tax_residencies: r.data.tax_residencies?.length ? r.data.tax_residencies : f.tax_residencies,
        }));
      }
    });
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));
  const setRes = (idx, k, v) => setForm((f) => ({ ...f, tax_residencies: f.tax_residencies.map((t, i) => (i === idx ? { ...t, [k]: v } : t)) }));
  const addRes = () => setForm((f) => ({ ...f, tax_residencies: [...f.tax_residencies, { country: "", tpin: "", id_type: "C" }] }));
  const removeRes = (idx) => setForm((f) => ({ ...f, tax_residencies: f.tax_residencies.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    setLoading(true);
    try {
      const { data } = await clientApi.post("/fatca", form);
      toast.success(data.remarks);
      setExisting(true);
      setForm((f) => ({ ...f, new_change: "C" }));
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="glass-card mt-6 max-w-2xl border-0">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 font-heading text-lg text-[var(--brand)]"><Globe2 className="h-4 w-4" /> FATCA / CRS Declaration</CardTitle>
          {existing && <StatusBadge status="UPLOADED" />}
        </div>
        <p className="text-xs text-[var(--ink-muted)]">Per the BSE FATCA UPLOAD structure. Mandatory for compliance reporting.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Input placeholder="Investor name" value={form.inv_name} onChange={set("inv_name")} data-testid="fatca-inv-name-input" />
          <Input placeholder="Place of birth" value={form.po_bir_inc} onChange={set("po_bir_inc")} data-testid="fatca-pob-input" />
          <Input placeholder="Country of birth" value={form.co_bir_inc} onChange={set("co_bir_inc")} data-testid="fatca-cob-input" />
          <select className="rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={form.pep_flag} onChange={set("pep_flag")} data-testid="fatca-pep-select">
            <option value="N">PEP: No</option>
            <option value="Y">PEP: Yes</option>
            <option value="R">PEP: Relative of PEP</option>
          </select>
          <select className="rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={form.srce_wealt} onChange={set("srce_wealt")} data-testid="fatca-wealth-select">
            {SOURCE_OF_WEALTH.map(([v, l]) => <option key={v} value={v}>Wealth: {l}</option>)}
          </select>
          <select className="rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={form.inc_slab} onChange={set("inc_slab")} data-testid="fatca-income-select">
            {INCOME_SLABS.map(([v, l]) => <option key={v} value={v}>Income: {l}</option>)}
          </select>
          <select className="rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm sm:col-span-2" value={form.occ_type} onChange={set("occ_type")} data-testid="fatca-occtype-select">
            <option value="S">Occupation type: Service</option>
            <option value="B">Occupation type: Business</option>
            <option value="O">Occupation type: Others</option>
            <option value="X">Occupation type: Not categorized</option>
          </select>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Tax residencies (max 4)</Label>
            {form.tax_residencies.length < 4 && (
              <Button type="button" size="sm" variant="outline" className="rounded-full text-xs" onClick={addRes} data-testid="fatca-add-residency-button">+ Add residency</Button>
            )}
          </div>
          {form.tax_residencies.map((t, idx) => (
            <div key={idx} className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[1fr_1fr_auto_auto]" data-testid={`fatca-residency-row-${idx}`}>
              <Input placeholder="Country" value={t.country} onChange={(e) => setRes(idx, "country", e.target.value)} data-testid={`fatca-residency-country-${idx}`} />
              <Input placeholder="TPIN / PAN" value={t.tpin} onChange={(e) => setRes(idx, "tpin", e.target.value.toUpperCase())} data-testid={`fatca-residency-tpin-${idx}`} />
              <select className="rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={t.id_type} onChange={(e) => setRes(idx, "id_type", e.target.value)}>
                <option value="C">PAN</option>
                <option value="A">Passport</option>
                <option value="B">Election ID</option>
                <option value="E">Driving License</option>
                <option value="G">UIDAI / Aadhaar</option>
              </select>
              {form.tax_residencies.length > 1 && (
                <Button type="button" size="sm" variant="ghost" className="text-red-600" onClick={() => removeRes(idx)}><Trash2 className="h-3.5 w-3.5" /></Button>
              )}
            </div>
          ))}
        </div>
        <Button className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" disabled={loading} onClick={handleSave} data-testid="fatca-save-button">
          <Save className="mr-2 h-4 w-4" /> {loading ? "Uploading..." : existing ? "Update FATCA (Change)" : "Upload FATCA"}
        </Button>
      </CardContent>
    </Card>
  );
}

function DocumentsCard() {
  const [docs, setDocs] = useState([]);
  const [flag, setFlag] = useState("UCC");
  const [uploading, setUploading] = useState(false);

  const load = () => clientApi.get("/documents").then((r) => setDocs(r.data));
  useEffect(() => { load(); }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const { data } = await clientApi.post("/documents/upload", {
        flag, file_name: file.name, file_bytes_b64: b64, document_type: "NRM",
      });
      toast.success(`${data.response_string} (Ref: ${data.document_id})`);
      load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <Card className="glass-card mt-6 max-w-2xl border-0">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-heading text-lg text-[var(--brand)]"><FileUp className="h-4 w-4" /> Document Uploads</CardTitle>
        <p className="text-xs text-[var(--ink-muted)]">AOF image / Cheque image (NRI-Minor) uploads per BSE Image Upload Services. Accepted: TIFF, JPG, PNG, PDF.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <select className="flex-1 rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={flag} onChange={(e) => setFlag(e.target.value)} data-testid="document-flag-select">
            <option value="UCC">AOF Image (Account Opening Form)</option>
            <option value="CHEQUE">Cheque Image (NRI / Minor)</option>
          </select>
          <label className={`flex min-h-11 cursor-pointer items-center justify-center gap-1 rounded-full bg-[var(--cta)] px-4 py-2 text-xs font-medium text-white sm:min-h-0 ${uploading ? "opacity-60" : ""}`}>
            <FileUp className="h-3.5 w-3.5" /> {uploading ? "Uploading..." : "Upload"}
            <input type="file" accept=".tiff,.tif,.jpg,.jpeg,.png,.pdf" className="hidden" onChange={handleFile} disabled={uploading} data-testid="document-file-input" />
          </label>
        </div>
        {docs.length > 0 && (
          <div className="divide-y divide-[var(--glass-border)] rounded-xl border border-[var(--glass-border)]">
            {docs.map((d) => (
              <div key={d.document_id} className="flex flex-col items-start gap-2 p-2.5 text-xs sm:flex-row sm:items-center sm:justify-between" data-testid={`document-row-${d.document_id}`}>
                <div className="flex min-w-0 items-center gap-2">
                  <FileText className="h-3.5 w-3.5 text-[var(--cta)]" />
                  <div>
                    <div className="font-medium">{d.file_name}</div>
                    <div className="text-[var(--ink-muted)]">{d.category}{d.mandate_id ? ` · Mandate #${d.mandate_id}` : ""} · Ref {d.document_id}</div>
                  </div>
                </div>
                <StatusBadge status={d.status} />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function NominationCard() {
  const [nomination, setNomination] = useState(null);
  const [nominees, setNominees] = useState([{ ...EMPTY_NOMINEE }]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    clientApi.get("/nomination").then((r) => {
      if (r.data) {
        setNomination(r.data);
        if (r.data.nominees?.length) setNominees(r.data.nominees);
      }
    });
  }, []);

  const updateNominee = (idx, field, value) => {
    setNominees((prev) => prev.map((n, i) => (i === idx ? { ...n, [field]: value } : n)));
  };

  const addNominee = () => setNominees((prev) => [...prev, { ...EMPTY_NOMINEE, relationship: "Son", allocation_percentage: 0 }]);
  const removeNominee = (idx) => setNominees((prev) => prev.filter((_, i) => i !== idx));

  const totalAllocation = nominees.reduce((sum, n) => sum + Number(n.allocation_percentage || 0), 0);

  const handleSave = async () => {
    setLoading(true);
    try {
      const { data } = await clientApi.post("/nomination", {
        nomination_opt: "Y",
        nominees: nominees.map((n) => ({ ...n, allocation_percentage: Number(n.allocation_percentage) })),
      });
      toast.success(data.remarks || "Nomination submitted to BSE");
      setNomination({ nominees, status: data.status || "PENDING_2FA" });
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="glass-card mt-6 max-w-2xl border-0">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-lg text-[var(--brand)]">Nomination</CardTitle>
          {nomination?.status && <StatusBadge status={nomination.status} />}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {nominees.map((n, idx) => (
          <div key={idx} className="space-y-2 rounded-xl border border-[var(--glass-border)] p-3" data-testid={`nominee-row-${idx}`}>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Input placeholder="Nominee name" value={n.name} onChange={(e) => updateNominee(idx, "name", e.target.value)} data-testid={`nominee-name-${idx}`} />
              <Input placeholder="Relationship" value={n.relationship} onChange={(e) => updateNominee(idx, "relationship", e.target.value)} data-testid={`nominee-relationship-${idx}`} />
              <Input placeholder="Allocation %" type="number" value={n.allocation_percentage} onChange={(e) => updateNominee(idx, "allocation_percentage", e.target.value)} data-testid={`nominee-allocation-${idx}`} />
              <Input placeholder="Mobile" value={n.mobile || ""} onChange={(e) => updateNominee(idx, "mobile", e.target.value)} data-testid={`nominee-mobile-${idx}`} />
              <Input placeholder="Email" value={n.email || ""} onChange={(e) => updateNominee(idx, "email", e.target.value)} data-testid={`nominee-email-${idx}`} />
              <div className="flex items-center gap-2">
                <Select value={n.id_proof_type || "PAN"} onValueChange={(v) => updateNominee(idx, "id_proof_type", v)}>
                  <SelectTrigger data-testid={`nominee-id-proof-type-${idx}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PAN">PAN</SelectItem>
                    <SelectItem value="Passport">Passport</SelectItem>
                    <SelectItem value="DrivingLicense">Driving License</SelectItem>
                    <SelectItem value="VoterID">Voter ID</SelectItem>
                    <SelectItem value="Aadhaar">Aadhaar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Input placeholder="ID proof number" value={n.id_proof_number || n.pan || ""} onChange={(e) => updateNominee(idx, "id_proof_number", e.target.value.toUpperCase())} data-testid={`nominee-id-proof-number-${idx}`} />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id={`minor-${idx}`} checked={!!n.is_minor} onCheckedChange={(v) => updateNominee(idx, "is_minor", v)} data-testid={`nominee-minor-checkbox-${idx}`} />
              <Label htmlFor={`minor-${idx}`} className="text-xs">This nominee is a minor</Label>
            </div>
            {n.is_minor && (
              <div className="grid grid-cols-1 gap-2 rounded-lg bg-[var(--surface-2)] p-2 sm:grid-cols-3">
                <Input placeholder="Guardian name" value={n.guardian_name || ""} onChange={(e) => updateNominee(idx, "guardian_name", e.target.value)} data-testid={`nominee-guardian-name-${idx}`} />
                <Input placeholder="Guardian PAN" value={n.guardian_pan || ""} onChange={(e) => updateNominee(idx, "guardian_pan", e.target.value.toUpperCase())} data-testid={`nominee-guardian-pan-${idx}`} />
                <Input placeholder="Guardian relationship" value={n.guardian_relationship || ""} onChange={(e) => updateNominee(idx, "guardian_relationship", e.target.value)} data-testid={`nominee-guardian-relationship-${idx}`} />
              </div>
            )}
            {nominees.length > 1 && (
              <Button type="button" size="sm" variant="ghost" className="text-xs text-red-600" onClick={() => removeNominee(idx)} data-testid={`nominee-remove-${idx}`}>
                <Trash2 className="mr-1 h-3.5 w-3.5" /> Remove
              </Button>
            )}
          </div>
        ))}
        <div className="flex flex-col items-start gap-2 text-xs text-[var(--ink-muted)] sm:flex-row sm:items-center sm:justify-between">
          <span>Total allocation: <span className={totalAllocation === 100 ? "font-semibold text-[var(--cta)]" : "font-semibold text-red-600"}>{totalAllocation}%</span></span>
          <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={addNominee} data-testid="nominee-add-button">
            <UserPlus className="mr-1 h-3.5 w-3.5" /> Add nominee
          </Button>
        </div>
        <Button className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" disabled={loading} onClick={handleSave} data-testid="nomination-save-button">
          <Save className="mr-2 h-4 w-4" /> {loading ? "Saving..." : "Save nomination"}
        </Button>
      </CardContent>

    </Card>
  );
}

export default function Profile() {
  const { user } = useAuth();
  const rows = [
    { label: "Full name", value: user?.full_name, icon: User },
    { label: "Email", value: user?.email, icon: Mail },
    { label: "Mobile", value: user?.phone, icon: Phone },
    { label: "Client Code (BSE)", value: user?.client_code || "Not assigned", icon: IdCard },
  ];
  return (
    <ClientLayout>
      <h1 className="mb-6 font-heading text-3xl text-[var(--brand)]">Profile</h1>
      <Card className="glass-card max-w-lg border-0">
        <CardContent className="space-y-4 p-6">
          {rows.map((r) => (
            <div key={r.label} className="flex items-center gap-3 border-b border-[var(--glass-border)] pb-3 last:border-0" data-testid={`profile-${r.label.toLowerCase().replace(/[\s()]+/g, "-")}`}>
              <r.icon className="h-4 w-4 text-[var(--cta)]" />
              <div>
                <div className="text-xs text-[var(--ink-muted)]">{r.label}</div>
                <div className="font-medium text-[var(--ink)]">{r.value}</div>
              </div>
            </div>
          ))}
          <div className="flex items-center gap-3">
            <span className="text-xs text-[var(--ink-muted)]">KYC Status</span>
            <StatusBadge status={user?.kyc_status} />
          </div>
        </CardContent>
      </Card>

      {user?.kyc_status === "APPROVED" && (
        <>
          <NominationCard />
          <FatcaCard user={user} />
          <DocumentsCard />
          <ServiceRequestsCard />
        </>
      )}
    </ClientLayout>
  );
}
